import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { Link } from "react-router-dom";

export default function HeroSlider({ slides }) {
  const { lang, t } = useLang();
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(timerRef.current);
  }, [slides]);

  if (!slides || slides.length === 0) {
    return <div className="h-[80vh] min-h-[600px] bg-[#0F2040]" />;
  }

  const cur = slides[idx];
  const title = cur[`title_${lang}`] || cur.title_es;
  const subtitle = cur[`subtitle_${lang}`] || cur.subtitle_es;
  const ctaLabel = cur[`cta_label_${lang}`] || cur.cta_label_es;

  return (
    <section className="relative h-[88vh] min-h-[640px] overflow-hidden bg-[#0F2040]" data-testid="hero-slider">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={s.image_url}
            alt=""
            className={`w-full h-full object-cover ${i === idx ? "animate-ken-burns" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2040]/85 via-[#0F2040]/55 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center">
        <div className="max-w-3xl text-white">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.3em] text-cecit-gold mb-6 animate-fade-up" data-testid="hero-kicker">
            <span className="editorial-rule mr-4 align-middle"></span>
            {t("hero.since")}
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-7 animate-fade-up" style={{animationDelay: "120ms"}} data-testid="hero-title">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed mb-10 animate-fade-up" style={{animationDelay: "240ms"}} data-testid="hero-subtitle">
              {subtitle}
            </p>
          )}
          <div className="flex flex-wrap gap-4 animate-fade-up" style={{animationDelay: "360ms"}}>
            {ctaLabel && cur.cta_url && (
              <Link to={cur.cta_url} className="inline-flex items-center bg-[#C8102E] hover:bg-[#A30D25] text-white px-8 py-4 text-sm font-medium tracking-wide transition-colors" data-testid="hero-cta-primary">
                {ctaLabel}
              </Link>
            )}
            <Link to="/la-camara" className="inline-flex items-center border border-white/40 hover:border-white text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-white/10 transition-colors" data-testid="hero-cta-secondary">
              {t("hero.learnMore")}
            </Link>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <div className="absolute bottom-10 left-6 lg:left-12 z-10 flex items-center gap-3">
            <button
              data-testid="hero-prev"
              onClick={() => setIdx((idx - 1 + slides.length) % slides.length)}
              className="w-11 h-11 border border-white/30 hover:bg-white hover:text-[#0F2040] text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-next"
              onClick={() => setIdx((idx + 1) % slides.length)}
              className="w-11 h-11 border border-white/30 hover:bg-white hover:text-[#0F2040] text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-xs tracking-widest text-white/70 ml-3">
              {String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
          </div>
          <div className="absolute bottom-10 right-6 lg:right-12 z-10 flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-0.5 transition-all ${i === idx ? "w-12 bg-cecit-gold" : "w-6 bg-white/30 hover:bg-white/60"}`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
